import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, TriangleAlert, X } from 'lucide-react';
import { Button, Card } from '../../../components/ui';
import { govDate, plural } from '../format';
import type { ApplyResult } from '../types';

/** What the results of applying say, one line per thing that happened */
export const resultLines = (
  results: ApplyResult[],
  nameOf: (nodeId: string) => string,
): Array<{ key: string; tone: 'ok' | 'warn'; node: React.ReactNode }> => {
  const lines: Array<{ key: string; tone: 'ok' | 'warn'; node: React.ReactNode }> = [];
  const record = (result: ApplyResult) =>
    result.organizationId ? (
      <Link to={`/organizations/${result.organizationId}`} className="text-ink underline decoration-neutral-300 underline-offset-2">
        {result.label ?? nameOf(result.nodeId)}
      </Link>
    ) : (
      <span className="text-ink">{nameOf(result.nodeId)}</span>
    );
  for (const result of results) {
    if (!result.ok) {
      lines.push({ key: `${result.nodeId}-error`, tone: 'warn', node: <>{nameOf(result.nodeId)}: {result.error}</> });
      continue;
    }
    const did = [
      result.recreated ? 'made again' : result.created ? 'created' : null,
      result.merged ? `${plural(result.merged, 'duplicate')} folded in` : null,
      result.renamed ? (result.renamed === 'official' ? 'renamed officially' : 'name corrected') : null,
      result.collapsedRoles ? `${plural(result.collapsedRoles, 'duplicate role')} removed` : null,
    ].filter(Boolean);
    if (did.length) {
      lines.push({ key: `${result.nodeId}-record`, tone: 'ok', node: <>{record(result)} — {did.join(', ')}</> });
    }
    const head = result.head;
    if (head && !head.ok) {
      lines.push({ key: `${result.nodeId}-head`, tone: 'warn', node: <>Head of {nameOf(result.nodeId)}: {head.error}</> });
    } else if (head && head.kind !== 'recorded') {
      lines.push({
        key: `${result.nodeId}-head`,
        tone: 'ok',
        node: (
          <>
            {head.stakeholderId ? (
              <Link to={`/stakeholders/${head.stakeholderId}`} className="text-ink underline decoration-neutral-300 underline-offset-2">
                {head.personLabel}
              </Link>
            ) : (
              head.personLabel
            )}
            {head.kind === 'correction' ? ' — start date corrected' : ` — ${head.role ?? 'head'}`}
            {head.since ? ` from ${govDate(head.since)}` : ''}
            {head.createdPerson ? ', added to the directory' : ''}
          </>
        ),
      });
    }
    for (const closed of head?.closed ?? []) {
      lines.push({
        key: `${result.nodeId}-${closed.assignmentId}`,
        tone: 'ok',
        node: (
          <>
            {closed.personLabel || 'A role'} — {closed.role} ended {govDate(closed.endDate)}
          </>
        ),
      });
    }
  }
  return lines;
};

/**
 * What applying just did, line by line, with links to every record and
 * person it touched — so "written to the directory" is something you can
 * go and look at, not something you take on trust.
 */
export const AppliedCard: React.FC<{
  results: ApplyResult[];
  nameOf: (nodeId: string) => string;
  onClose: () => void;
}> = ({ results, nameOf, onClose }) => {
  const [all, setAll] = useState(false);
  const lines = resultLines(results, nameOf);
  const ok = results.filter((result) => result.ok).length;
  const failed = results.length - ok;
  const shown = all ? lines : lines.slice(0, 6);
  return (
    <Card className="mb-4 animate-fade-up px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Check className="h-4 w-4 flex-shrink-0 text-emerald-600" strokeWidth={2} />
        <p className="min-w-0 flex-1 text-[13px] text-ink">
          {plural(ok, 'entity', 'entities')} brought up to date
          {failed ? <span className="text-amber-700"> · {failed} could not be</span> : null}
        </p>
        <Button size="xs" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
      {lines.length === 0 ? (
        <p className="mt-1 pl-7 text-[12.5px] text-neutral-500">Nothing needed changing — the directory already matched.</p>
      ) : (
        <ul className="mt-1.5 space-y-1 pl-7">
          {shown.map((line) => (
            <li
              key={line.key}
              className={`flex items-start gap-1.5 text-[12.5px] leading-snug ${
                line.tone === 'warn' ? 'text-amber-700' : 'text-neutral-600'
              }`}
            >
              {line.tone === 'warn' ? (
                <TriangleAlert className="mt-px h-3 w-3 flex-shrink-0" strokeWidth={1.75} />
              ) : (
                <span className="mt-[7px] h-1 w-1 flex-shrink-0 rounded-full bg-emerald-600" />
              )}
              <span className="min-w-0">{line.node}</span>
            </li>
          ))}
        </ul>
      )}
      {lines.length > 6 && (
        <button
          type="button"
          onClick={() => setAll((value) => !value)}
          className="mt-1 pl-7 text-[12px] text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
        >
          {all ? 'Show less' : `Show all ${lines.length}`}
        </button>
      )}
    </Card>
  );
};

/**
 * What "Update record" just did, shown where it was pressed: at the foot of
 * the panel, next to the button, rather than at the top of a page the reader
 * has scrolled away from.
 */
export const OutcomeNote: React.FC<{
  result: ApplyResult;
  name: string;
  onClose: () => void;
}> = ({ result, name, onClose }) => {
  const lines = resultLines([result], () => name);
  return (
    <div
      role="status"
      className={`animate-fade-up border-t px-4 py-2.5 text-[12.5px] ${
        result.ok ? 'border-emerald-100 bg-emerald-50/70' : 'border-amber-100 bg-amber-50/70'
      }`}
    >
      <div className="flex items-start gap-2">
        {result.ok ? (
          <Check className="mt-px h-3.5 w-3.5 flex-shrink-0 text-emerald-600" strokeWidth={2.25} />
        ) : (
          <TriangleAlert className="mt-px h-3.5 w-3.5 flex-shrink-0 text-amber-600" strokeWidth={1.75} />
        )}
        <div className="min-w-0 flex-1 space-y-0.5 text-neutral-600">
          <p className={result.ok ? 'text-emerald-800' : 'text-amber-800'}>
            {result.ok ? (lines.length ? 'Done' : 'Done — nothing needed changing') : 'Not applied'}
          </p>
          {lines.map((line) => (
            <p key={line.key} className={line.tone === 'warn' ? 'text-amber-800' : ''}>
              {line.node}
            </p>
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-white/70 hover:text-ink"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
};
